/**
 * Module 01 — Cloud POS connection semantics.
 *
 * Cloud POS is derived ONLY from confirmed quds_pos_cloud TENANT / BRANCH links.
 * License, updater, and owner links must never imply Cloud POS Connected.
 */
export const CLOUD_POS_STATUSES = ['connected', 'partially_connected', 'not_connected'];
export const CLOUD_POS_STATUS_LABELS = {
    connected: 'Connected',
    partially_connected: 'Partially Connected',
    not_connected: 'Not Connected',
};
/**
 * Normalize Cloud POS list state for UI rendering.
 * Missing/partial payloads must never crash the Pharmacies grid.
 */
export function resolveCloudPosListState(cloudPos, caBranchCount = 0) {
    const status = cloudPos?.status && CLOUD_POS_STATUSES.includes(cloudPos.status)
        ? cloudPos.status
        : 'not_connected';
    return {
        status,
        cloudBranchCount: typeof cloudPos?.cloudBranchCount === 'number' ? cloudPos.cloudBranchCount : 0,
        caBranchCount: typeof cloudPos?.caBranchCount === 'number' ? cloudPos.caBranchCount : Math.max(0, caBranchCount),
    };
}
/**
 * Business-level Cloud POS status.
 *
 * - not_connected: no confirmed primary TENANT
 * - partially_connected: TENANT present but zero confirmed BRANCH links,
 *   or CA branches exist that are not yet mapped (when caBranchCount > cloudBranchCount)
 * - connected: TENANT + at least one BRANCH, and no unmapped CA branches
 *   (or no CA branches at all — Production-only import)
 */
export function computeCloudPosSummary(input) {
    const confirmed = input.links.filter((l) => (l.status ?? 'confirmed') === 'confirmed' && (l.role ?? 'primary') === 'primary');
    const cloud = confirmed.filter((l) => l.system === 'quds_pos_cloud');
    const hasTenant = cloud.some((l) => l.entityType === 'TENANT');
    const cloudBranchCount = cloud.filter((l) => l.entityType === 'BRANCH').length;
    const caBranchCount = Math.max(0, input.caBranchCount);
    if (!hasTenant) {
        return { status: 'not_connected', hasTenant: false, cloudBranchCount, caBranchCount };
    }
    if (cloudBranchCount === 0) {
        return { status: 'partially_connected', hasTenant: true, cloudBranchCount, caBranchCount };
    }
    // Unmapped CA directory branches → partial (operator still needs Connect).
    if (caBranchCount > 0 && cloudBranchCount < caBranchCount) {
        return { status: 'partially_connected', hasTenant: true, cloudBranchCount, caBranchCount };
    }
    return { status: 'connected', hasTenant: true, cloudBranchCount, caBranchCount };
}
export const BRANCH_API_STATE_LABELS = {
    not_applicable: '—',
    not_configured: 'Not Configured',
    configured: 'Configured',
    configured_recoverable: 'Configured · Recoverable',
    configured_not_recoverable: 'Configured · Not Recoverable',
};
//# sourceMappingURL=cloud-pos.js.map